/**
 * dexascan-booking-override.js
 *
 * Replaces window.DexascanWidget.init with a public-safe implementation
 * that calls getAvailableSlots (no user auth required) instead of
 * getCalendarAvailability (which requires provider login and hangs on Shopify).
 *
 * Drop this script AFTER dexascan-widget-v20.umd.js in the theme so it
 * overrides window.DexascanWidget before the inline init fires on 'load'.
 *
 * postMessage format expected by booking-interceptor.js:
 *   [6, [dateString, timeString, locationName]]
 */
(function () {
  'use strict';

  var APP_ID   = '696850ebdf9149f449a85e37';
  var BASE_URL = 'https://dexaconnect.io';
  var BOOKING_TYPE = 6;

  function pad(n) { return String(n).padStart(2, '0'); }

  function isoDate(d) {
    return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
  }

  function formatDisplayDate(isoStr) {
    var d = new Date(isoStr + 'T12:00:00');
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  }

  var CSS = [
    '.dxa-wrap{font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;',
    'color:#1a1a2e;background:#fff;border:1px solid #e2e8f0;border-radius:12px;',
    'padding:20px;max-width:480px;margin:0 auto;box-shadow:0 2px 8px rgba(0,0,0,.06)}',
    '.dxa-title{font-size:16px;font-weight:600;margin:0 0 16px;display:flex;',
    'align-items:center;gap:8px}',
    '.dxa-title svg{flex-shrink:0}',
    '.dxa-label{font-size:12px;font-weight:600;text-transform:uppercase;',
    'letter-spacing:.05em;color:#64748b;margin:0 0 8px}',
    '.dxa-section{margin-bottom:16px}',
    '.dxa-date-nav{display:flex;align-items:center;justify-content:space-between;',
    'background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:8px 12px}',
    '.dxa-date-nav span{font-size:14px;font-weight:500}',
    '.dxa-nav-btn{background:none;border:none;cursor:pointer;padding:4px 8px;',
    'border-radius:6px;font-size:18px;line-height:1;color:#475569}',
    '.dxa-nav-btn:hover{background:#e2e8f0}',
    '.dxa-nav-btn:disabled{opacity:.35;cursor:default}',
    '.dxa-slots{display:grid;grid-template-columns:repeat(3,1fr);gap:8px;margin-top:8px}',
    '.dxa-slot{border:1px solid #e2e8f0;background:#fff;border-radius:8px;',
    'padding:8px 4px;font-size:13px;font-weight:500;cursor:pointer;',
    'text-align:center;transition:all .15s}',
    '.dxa-slot:hover{border-color:#f97316;color:#f97316}',
    '.dxa-slot.selected{background:#f97316;border-color:#f97316;color:#fff}',
    '.dxa-slot:disabled{opacity:.4;cursor:not-allowed}',
    '.dxa-empty{font-size:13px;color:#94a3b8;text-align:center;padding:16px 0}',
    '.dxa-spinner{display:flex;justify-content:center;padding:20px 0}',
    '.dxa-spinner-ring{width:28px;height:28px;border:3px solid #e2e8f0;',
    'border-top-color:#f97316;border-radius:50%;animation:dxa-spin .7s linear infinite}',
    '@keyframes dxa-spin{to{transform:rotate(360deg)}}',
    '.dxa-confirm{background:#f0fdf4;border:1px solid #bbf7d0;border-radius:8px;',
    'padding:10px 14px;font-size:13px;color:#166534;font-weight:500;margin-top:12px}',
    '.dxa-error{background:#fef2f2;border:1px solid #fecaca;border-radius:8px;',
    'padding:10px 14px;font-size:13px;color:#991b1b;margin-top:8px}',
  ].join('');

  function fetchSlots(clinicId, dateStr, cb) {
    var url = BASE_URL + '/api/apps/' + APP_ID + '/functions/getAvailableSlots';
    var xhr = new XMLHttpRequest();
    xhr.open('POST', url, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.onload = function () {
      try { cb(null, JSON.parse(xhr.responseText)); } catch (e) { cb(e, null); }
    };
    xhr.onerror = function () { cb(new Error('Network error'), null); };
    xhr.send(JSON.stringify({ clinic_id: clinicId, date: dateStr }));
  }

  function mount(containerId, opts) {
    var container = document.getElementById(containerId);
    if (!container) { console.warn('[DexaScan] container not found:', containerId); return; }

    if (!document.getElementById('dxa-override-styles')) {
      var style = document.createElement('style');
      style.id = 'dxa-override-styles';
      style.textContent = CSS;
      document.head.appendChild(style);
    }

    var today = new Date(); today.setHours(0, 0, 0, 0);
    var selectedDate = new Date(today);
    var selectedSlot = null;
    var clinicName = opts.providerName || opts.location || 'Clinic';

    function maxDate() { var d = new Date(today); d.setDate(d.getDate() + 90); return d; }

    function render(slots, loading, error) {
      var dateStr = isoDate(selectedDate);
      var isPrev = selectedDate <= today;
      var isNext = selectedDate >= maxDate();
      var slotHtml = '';
      if (loading) {
        slotHtml = '<div class="dxa-spinner"><div class="dxa-spinner-ring"></div></div>';
      } else if (error) {
        slotHtml = '<div class="dxa-error">Could not load availability. Please try again.</div>';
      } else if (!slots || slots.length === 0) {
        slotHtml = '<div class="dxa-empty">No available times on this date.</div>';
      } else {
        var btns = slots.map(function (s) {
          var sel = selectedSlot && selectedSlot.time === s.time ? ' selected' : '';
          return '<button class="dxa-slot' + sel + '" data-time="' + s.time + '">' + s.time + '</button>';
        }).join('');
        slotHtml = '<div class="dxa-slots">' + btns + '</div>';
      }
      var confirmHtml = '';
      if (selectedSlot) {
        confirmHtml = '<div class="dxa-confirm">\u2713 ' + formatDisplayDate(dateStr) + ' at ' + selectedSlot.time + '</div>';
      }
      container.innerHTML = [
        '<div class="dxa-wrap">',
        '<p class="dxa-title"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f97316" stroke-width="2">',
        '<rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/>',
        '<line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>',
        'Select a Date &amp; Time</p>',
        '<div class="dxa-section"><p class="dxa-label">Date</p>',
        '<div class="dxa-date-nav">',
        '<button class="dxa-nav-btn" id="dxa-prev"' + (isPrev ? ' disabled' : '') + '>&#8592;</button>',
        '<span>' + formatDisplayDate(dateStr) + '</span>',
        '<button class="dxa-nav-btn" id="dxa-next"' + (isNext ? ' disabled' : '') + '>&#8594;</button>',
        '</div></div>',
        '<div class="dxa-section"><p class="dxa-label">Available Times</p>' + slotHtml + '</div>',
        confirmHtml,
        '</div>',
      ].join('');
      var prevBtn = document.getElementById('dxa-prev');
      var nextBtn = document.getElementById('dxa-next');
      if (prevBtn) prevBtn.addEventListener('click', function () { selectedDate.setDate(selectedDate.getDate() - 1); selectedSlot = null; loadSlots(); });
      if (nextBtn) nextBtn.addEventListener('click', function () { selectedDate.setDate(selectedDate.getDate() + 1); selectedSlot = null; loadSlots(); });
      container.querySelectorAll('.dxa-slot').forEach(function (btn) {
        btn.addEventListener('click', function () {
          selectedSlot = { time: btn.getAttribute('data-time') };
          window.dispatchEvent(new MessageEvent('message', { data: [BOOKING_TYPE, [isoDate(selectedDate), selectedSlot.time, clinicName]] }));
          render(slots, false, null);
        });
      });
    }

    function loadSlots() {
      render(null, true, null);
      fetchSlots(opts.serviceId, isoDate(selectedDate), function (err, data) {
        if (err || !data || !data.success) { render(null, false, true); return; }
        if (data.clinic && data.clinic.name) clinicName = data.clinic.name;
        render(data.slots || [], false, null);
      });
    }
    loadSlots();
  }

  function installOverride() {
    if (!window.DexascanWidget) window.DexascanWidget = {};
    window.DexascanWidget.init = function (containerId, opts) {
      if (!opts || !opts.serviceId) { console.warn('[DexaScan] serviceId missing'); return; }
      console.log('[DexaScan] booking override active — serviceId:', opts.serviceId);
      mount(containerId, opts);
    };
    console.log('[DexaScan] booking-override installed \u2713');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', installOverride);
  } else {
    installOverride();
  }
})();